/**
 * Enums y utilidades clínicas para el sistema CGT
 * Incluye patrones clínicos, escalas y criterios médicos
 */

import { ClinicalPattern, AnatomicalSystem } from './medical';

// ═══════════════════════════════════════════════════════════════════════════════
// PATRONES CLÍNICOS AUTODETECTABLES
// ═══════════════════════════════════════════════════════════════════════════════

export const CLINICAL_PATTERNS: Record<string, ClinicalPattern> = {
  // ─────────────────────────────────────────────────────────────────────────────
  // TRIADAS CARDIOVASCULARES
  // ─────────────────────────────────────────────────────────────────────────────
  
  BECK_TRIAD: {
    id: 'beck_triad',
    name: 'Triada de Beck',
    description: 'Signos de tamponade cardíaco: hipotensión, distensión yugular, ruidos cardíacos ahogados',
    nodeCriteria: [
      { conceptCodes: ['45007003'], required: true }, // Hypotension
      { conceptCodes: ['271823003'], required: true }, // Jugular venous distension
      { conceptCodes: ['86208002'], required: true }, // Muffled heart sounds
    ],
    urgency: 'immediate',
    condition: 'Tamponade cardíaco / Neumotórax a tensión',
    confidenceThreshold: 0.9
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // TRIADAS HEPATOBILIARES
  // ─────────────────────────────────────────────────────────────────────────────
  
  CHARCOT_TRIAD: {
    id: 'charcot_triad',
    name: 'Tríada de Charcot',
    description: 'Dolor en cuadrante superior derecho, fiebre e ictericia',
    nodeCriteria: [
      { conceptCodes: ['14760008'], required: true }, // RUQ pain
      { conceptCodes: ['386661006'], required: true }, // Fever
      { conceptCodes: ['18165001'], required: true }, // Jaundice
    ],
    urgency: 'immediate',
    condition: 'Colédoco-litiasis aguda / Colangitis',
    confidenceThreshold: 0.9
  },

  REYNOLDS_PENTAD: {
    id: 'reynolds_pentad',
    name: 'Pentada de Reynolds',
    description: 'Charcot + confusión y shock',
    nodeCriteria: [
      { conceptCodes: ['14760008'], required: true },
      { conceptCodes: ['386661006'], required: true },
      { conceptCodes: ['18165001'], required: true },
      { conceptCodes: ['40917007'], required: true }, // Confusion
      { conceptCodes: ['45007003'], required: true }, // Hypotension (shock)
    ],
    urgency: 'immediate',
    condition: 'Colangitis severa con sepsis',
    confidenceThreshold: 1.0
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // TRIADAS NEUROLÓGICAS
  // ─────────────────────────────────────────────────────────────────────────────
  
  CUSHING_TRIAD: {
    id: 'cushing_triad',
    name: 'Triada de Cushing',
    description: 'Hipertensión, bradicardia y respiración irregular',
    nodeCriteria: [
      { conceptCodes: ['38341003'], required: true }, // Hypertension
      { conceptCodes: ['48867003'], required: true }, // Bradycardia
      { conceptCodes: ['267036007'], required: true }, // Irregular respiration
    ],
    urgency: 'immediate',
    condition: 'Hipertensión intracraneal severa / Hermiación',
    confidenceThreshold: 0.9
  },

  MENINGITIS_TRIAD: {
    id: 'meningitis_triad',
    name: 'Triada de Meningitis',
    description: 'Fiebre, cefalea y rigidez de nuca',
    nodeCriteria: [
      { conceptCodes: ['386661006'], required: true },
      { conceptCodes: ['25064002'], required: true }, // Headache
      { conceptCodes: ['250111002'], required: true }, // Neck stiffness
    ],
    urgency: 'immediate',
    condition: 'Meningitis bacteriana',
    confidenceThreshold: 0.85
  },

  // ─────────────────────────────────════════════════════════════════════════════
  // CRITERIOS DE SEPSIS
  // ─────────────────════════════════════════════════════════════════════════════
  
  SEPSIS_CRITERIA: {
    id: 'sepsis_criteria',
    name: 'Sepsis (Sepsis-3)',
    description: 'Infección sospechada + qSOFA ≥ 2',
    nodeCriteria: [
      { nodeType: 'symptom', required: true }, // Suspected infection
      { 
        conceptCodes: ['407389004'], // Altered mental status
        severity: ['severe', 'life-threatening'],
        required: true 
      },
    ],
    urgency: 'immediate',
    condition: 'Sepsis / Shock séptico',
    confidenceThreshold: 0.8
  },

  SEPTIC_SHOCK: {
    id: 'septic_shock',
    name: 'Shock Séptico',
    description: 'Sepsis + lactato elevado + vasopresores',
    nodeCriteria: [
      { conceptCodes: ['91302008'], required: true }, // Sepsis
      { 
        conceptCodes: ['1968-7'], // Lactate
        valuePredicate: (v: unknown) => typeof v === 'number' && v > 2.0,
        required: true 
      },
      { conceptCodes: ['45007003'], required: true }, // Hypotension
    ],
    urgency: 'immediate',
    condition: 'Shock séptico refractario',
    confidenceThreshold: 0.95
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // SÍNDROME CORONARIO AGUDO
  // ─────────────────────────────────────────────────────────────────────────────
  
  STEMI_PATTERN: {
    id: 'stemi_pattern',
    name: 'STEMI',
    description: 'IAM con elevación del ST',
    nodeCriteria: [
      { 
        conceptCodes: ['279035001'], // Chest pain
        severity: ['severe', 'life-threatening'],
        required: true 
      },
      { 
        conceptCodes: ['10839-9'], // Troponin I
        valuePredicate: (v: unknown) => typeof v === 'number' && v > 0.04,
        required: true 
      },
      { conceptCodes: ['22298006'], required: false }, // MI diagnosis
    ],
    urgency: 'immediate',
    condition: 'Infarto agudo de miocardio con elevación ST',
    confidenceThreshold: 0.9
  },

  NSTEMI_PATTERN: {
    id: 'nstemi_pattern',
    name: 'NSTEMI',
    description: 'IAM sin elevación del ST',
    nodeCriteria: [
      { conceptCodes: ['279035001'], required: true },
      { 
        conceptCodes: ['10839-9'],
        valuePredicate: (v: unknown) => typeof v === 'number' && v > 0.04,
        required: true 
      },
    ],
    urgency: 'immediate',
    condition: 'Infarto agudo de miocardio sin elevación ST',
    confidenceThreshold: 0.85
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // EPOC EXACERBADO
  // ─────────────────────────────────────────────────────────────────────────────
  
  COPD_EXACERBATION: {
    id: 'copd_exacerbation',
    name: 'EPOC Exacerbado',
    description: 'EPOC con empeoramiento agudo',
    nodeCriteria: [
      { conceptCodes: ['13645005'], required: true }, // COPD
      { 
        conceptCodes: ['9279-1'], // Respiratory rate
        valuePredicate: (v: unknown) => typeof v === 'number' && v > 24,
        required: true 
      },
      { 
        conceptCodes: ['2708-6'], // SpO2
        valuePredicate: (v: unknown) => typeof v === 'number' && v < 90,
        required: false 
      },
    ],
    urgency: 'delayed',
    condition: 'Exacerbación aguda de EPOC',
    confidenceThreshold: 0.75
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // PULMONAR
  // ─────────────────────────────────────────────────────────────────────────────
  
  PULMONARY_EMBOLISM: {
    id: 'pulmonary_embolism',
    name: 'Tromboembolia Pulmonar',
    description: 'Disnea súbita + taquicardia + hipoxemia',
    nodeCriteria: [
      { 
        conceptCodes: ['267036007'], // Dyspnea
        severity: ['severe'],
        required: true 
      },
      { 
        conceptCodes: ['8867-4'], // Heart rate
        valuePredicate: (v: unknown) => typeof v === 'number' && v > 100,
        required: true 
      },
      { 
        conceptCodes: ['2708-6'], // SpO2
        valuePredicate: (v: unknown) => typeof v === 'number' && v < 90,
        required: true 
      },
    ],
    urgency: 'immediate',
    condition: 'Embolia pulmonar',
    confidenceThreshold: 0.8
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // METABÓLICO
  // ─────────────────────────────────────────────────────────────────────────────
  
  DKA_PATTERN: {
    id: 'dka_pattern',
    name: 'Cetoacidosis Diabética',
    description: 'Glucosa elevada + acidosis + cetonemia',
    nodeCriteria: [
      { 
        conceptCodes: ['2345-7'], // Glucose
        valuePredicate: (v: unknown) => typeof v === 'number' && v > 250,
        required: true 
      },
      { 
        conceptCodes: ['2744-1'], // pH
        valuePredicate: (v: unknown) => typeof v === 'number' && v < 7.3,
        required: true 
      },
      { conceptCodes: ['237620003'], required: false }, // Ketones
    ],
    urgency: 'immediate',
    condition: 'Cetoacidosis diabética',
    confidenceThreshold: 0.85
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // SÍNDROMES DE DIFICULTAD RESPIRATORIA
  // ─────────────────────────────────────────────────────────────────────────────
  
  ARDS_CRITERIA: {
    id: 'ards_criteria',
    name: 'SDRA',
    description: 'Síndrome de distrés respiratorio agudo',
    nodeCriteria: [
      { 
        conceptCodes: ['267036007'], // Dyspnea
        severity: ['severe', 'life-threatening'],
        required: true 
      },
      { 
        conceptCodes: ['2703-7'], // PaO2
        valuePredicate: (v: unknown) => typeof v === 'number' && v < 60,
        required: true 
      },
      { 
        conceptCodes: ['2019-8'], // PaCO2
        required: false 
      },
    ],
    urgency: 'immediate',
    condition: 'SDRA (Síndrome de distrés respiratorio agudo)',
    confidenceThreshold: 0.85
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// ESCALAS CLÍNICAS
// ═══════════════════════════════════════════════════════════════════════════════

export enum ClinicalScale {
  NEWS2 = 'news2',           // National Early Warning Score
  qSOFA = 'qsofa',           // Quick SOFA
  GCS = 'gcs',               // Glasgow Coma Scale
  KILLIP = 'killip',         // Killip Classification
  APGAR = 'apgar',           // APGAR Score
  TIMI = 'timi'              // TIMI Risk Score
}

export interface ScaleScore {
  scale: ClinicalScale;
  total: number;
  components: ScaleComponent[];
  risk: 'low' | 'moderate' | 'high' | 'critical';
  interpretation: string;
}

export interface ScaleComponent {
  name: string;
  value: number | string;
  points: number;
}

// NEWS2 Score interpretation
export function calculateNEWS2(
  respiratoryRate: number,
  spo2: number,
  systolicBP: number,
  pulse: number,
  consciousness: 'alert' | 'verbal' | 'pain' | 'unresponsive',
  temperature: number
): ScaleScore {
  const components: ScaleComponent[] = [];
  let total = 0;

  // Respiratory rate
  let rrPoints = 0;
  if (respiratoryRate <= 8 || respiratoryRate >= 25) rrPoints = 3;
  else if (respiratoryRate >= 21) rrPoints = 2;
  else if (respiratoryRate >= 9 && respiratoryRate <= 11) rrPoints = 1;
  components.push({ name: 'Respiratory Rate', value: respiratoryRate, points: rrPoints });
  total += rrPoints;

  // SpO2
  let spo2Points = 0;
  if (spo2 <= 91) spo2Points = 3;
  else if (spo2 <= 93) spo2Points = 2;
  else if (spo2 <= 95) spo2Points = 1;
  components.push({ name: 'SpO2', value: spo2, points: spo2Points });
  total += spo2Points;

  // Systolic BP
  let bpPoints = 0;
  if (systolicBP <= 90 || systolicBP >= 220) bpPoints = 3;
  else if (systolicBP >= 91 && systolicBP <= 100) bpPoints = 2;
  else if (systolicBP >= 101 && systolicBP <= 110) bpPoints = 1;
  components.push({ name: 'Systolic BP', value: systolicBP, points: bpPoints });
  total += bpPoints;

  // Pulse
  let pulsePoints = 0;
  if (pulse <= 40 || pulse >= 131) pulsePoints = 3;
  else if (pulse >= 111) pulsePoints = 2;
  else if (pulse >= 41 && pulse <= 50) pulsePoints = 1;
  components.push({ name: 'Heart Rate', value: pulse, points: pulsePoints });
  total += pulsePoints;

  // Consciousness
  const gcsPoints = consciousness === 'alert' ? 0 : 3;
  components.push({ name: 'Consciousness', value: consciousness, points: gcsPoints });
  total += gcsPoints;

  // Temperature
  let tempPoints = 0;
  if (temperature <= 35.0) tempPoints = 3;
  else if (temperature >= 39.1) tempPoints = 2;
  else if (temperature >= 38.1 && temperature <= 39.0) tempPoints = 1;
  else if (temperature >= 35.1 && temperature <= 36.0) tempPoints = 1;
  components.push({ name: 'Temperature', value: temperature, points: tempPoints });
  total += tempPoints;

  // Risk interpretation
  let risk: 'low' | 'moderate' | 'high' | 'critical';
  let interpretation: string;
  
  if (total === 0) {
    risk = 'low';
    interpretation = 'Riesgo bajo - monitoreo estándar';
  } else if (total >= 1 && total <= 4) {
    risk = 'low';
    interpretation = 'Riesgo bajo - alerta al médico si puntuación de 3 en un parámetro';
  } else if (total >= 5 && total <= 6) {
    risk = 'moderate';
    interpretation = 'Riesgo moderado - evaluación médica urgente';
  } else {
    risk = 'critical';
    interpretation = 'Riesgo alto - respuesta médica de emergencia';
  }

  return { scale: ClinicalScale.NEWS2, total, components, risk, interpretation };
}

// qSOFA Score
export function calculateqSOFA(
  respiratoryRate: number,
  systolicBP: number,
  alteredMentalStatus: boolean
): ScaleScore {
  const components: ScaleComponent[] = [];
  let total = 0;

  // RR ≥ 22
  const rrPoints = respiratoryRate >= 22 ? 1 : 0;
  components.push({ name: 'Respiratory Rate ≥ 22', value: respiratoryRate, points: rrPoints });
  total += rrPoints;

  // SBP ≤ 100
  const bpPoints = systolicBP <= 100 ? 1 : 0;
  components.push({ name: 'Systolic BP ≤ 100', value: systolicBP, points: bpPoints });
  total += bpPoints;

  // Altered mental status
  const gcsPoints = alteredMentalStatus ? 1 : 0;
  components.push({ name: 'Altered Mental Status', value: alteredMentalStatus ? 'Yes' : 'No', points: gcsPoints });
  total += gcsPoints;

  const risk: 'low' | 'moderate' | 'high' | 'critical' = total >= 2 ? 'high' : 'low';
  const interpretation = total >= 2 
    ? 'Alto riesgo de mortalidad por sepsis - considerar ICU'
    : 'Riesgo estándar - evaluar criterios clínicos adicionales';

  return { scale: ClinicalScale.qSOFA, total, components, risk, interpretation };
}

// Killip Classification
export function calculateKillip(
  hasRales: boolean,
  hasS3: boolean,
  hasHypotension: boolean,
  hasShock: boolean
): ScaleScore {
  let killipClass: number;
  let interpretation: string;

  if (hasShock) {
    killipClass = 4;
    interpretation = 'Killip IV: Shock cardiogénico';
  } else if (hasHypotension && hasRales) {
    killipClass = 3;
    interpretation = 'Killip III: Edema agudo de pulmón severo';
  } else if (hasRales || hasS3) {
    killipClass = 2;
    interpretation = 'Killip II: Insuficiencia cardíaca leve-moderada';
  } else {
    killipClass = 1;
    interpretation = 'Killip I: Sin insuficiencia cardíaca';
  }

  return {
    scale: ClinicalScale.KILLIP,
    total: killipClass,
    components: [
      { name: 'Rales', value: hasRales ? 'Present' : 'Absent', points: hasRales ? 1 : 0 },
      { name: 'S3 Gallop', value: hasS3 ? 'Present' : 'Absent', points: hasS3 ? 1 : 0 },
      { name: 'Hypotension', value: hasHypotension ? 'Present' : 'Absent', points: hasHypotension ? 1 : 0 },
      { name: 'Cardiogenic Shock', value: hasShock ? 'Present' : 'Absent', points: hasShock ? 1 : 0 },
    ],
    risk: killipClass >= 3 ? 'critical' : killipClass === 2 ? 'high' : 'low',
    interpretation
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// RANGOS NORMALES POR EDAD/SEXO
// ═══════════════════════════════════════════════════════════════════════════════

export interface NormalRange {
  low?: number;
  high?: number;
  unit: string;
  ageMin?: number;
  ageMax?: number;
  sex?: 'male' | 'female' | 'all';
}

export const NORMAL_RANGES: Record<string, NormalRange[]> = {
  '8867-4': [ // Heart rate
    { low: 60, high: 100, unit: 'beats/min', ageMin: 18, sex: 'all' },
    { low: 60, high: 100, unit: 'beats/min', ageMin: 12, ageMax: 17, sex: 'all' },
    { low: 70, high: 110, unit: 'beats/min', ageMin: 6, ageMax: 11, sex: 'all' },
    { low: 80, high: 120, unit: 'beats/min', ageMin: 3, ageMax: 5, sex: 'all' },
    { low: 90, high: 140, unit: 'beats/min', ageMin: 1, ageMax: 2, sex: 'all' },
  ],
  '8480-6': [ // Systolic BP
    { low: 90, high: 120, unit: 'mmHg', ageMin: 18, sex: 'all' },
    { low: 100, high: 120, unit: 'mmHg', ageMin: 12, ageMax: 17, sex: 'all' },
  ],
  '9279-1': [ // Respiratory rate
    { low: 12, high: 20, unit: 'breaths/min', ageMin: 18, sex: 'all' },
    { low: 12, high: 20, unit: 'breaths/min', ageMin: 12, ageMax: 17, sex: 'all' },
    { low: 18, high: 30, unit: 'breaths/min', ageMin: 6, ageMax: 11, sex: 'all' },
    { low: 20, high: 30, unit: 'breaths/min', ageMin: 3, ageMax: 5, sex: 'all' },
  ],
  '8310-5': [ // Temperature
    { low: 36.1, high: 37.2, unit: 'C', ageMin: 0, sex: 'all' },
  ],
  '2708-6': [ // SpO2
    { low: 95, high: 100, unit: '%', ageMin: 0, sex: 'all' },
  ],
  '6598-7': [ // Troponin T
    { low: 0, high: 14, unit: 'ng/L', ageMin: 0, sex: 'all' },
  ],
  '10839-9': [ // Troponin I
    { low: 0, high: 0.04, unit: 'ng/mL', ageMin: 0, sex: 'all' },
  ],
  '1968-7': [ // Lactate
    { low: 0.5, high: 2.0, unit: 'mmol/L', ageMin: 0, sex: 'all' },
  ],
};

export function getNormalRange(
  loincCode: string,
  age: number,
  sex: 'male' | 'female' | 'other'
): NormalRange | undefined {
  const ranges = NORMAL_RANGES[loincCode];
  if (!ranges) return undefined;

  return ranges.find(r => 
    (r.ageMin === undefined || age >= r.ageMin) &&
    (r.ageMax === undefined || age <= r.ageMax) &&
    (r.sex === undefined || r.sex === 'all' || r.sex === sex)
  ) || ranges[ranges.length - 1];
}

// ═══════════════════════════════════════════════════════════════════════════════
// COLORES CLÍNICOS (hex strings — para CSS/DOM)
// Para colores Three.js (number hex) ver core/config.ts
// ═══════════════════════════════════════════════════════════════════════════════

export const ANATOMICAL_COLORS: Record<AnatomicalSystem, string> = {
  cardiovascular: '#EF4444',
  respiratory: '#3B82F6',
  neurological: '#F59E0B',
  gastrointestinal: '#10B981',
  renal: '#8B5CF6',
  metabolic: '#EC4899',
  infectious: '#14B8A6',
  musculoskeletal: '#F97316',
  endocrine: '#84CC16',
  hematologic: '#06B6D4',
};
